if (!customElements.get('product-rebuy-bundle')) {
  customElements.define(
    'product-rebuy-bundle',
    class ProductRebuyBundle extends HTMLElement {
      constructor() {
        super();

        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleSellingPlanChange = this.handleSellingPlanChange.bind(this);
        this.handleRebuyReady = this.handleRebuyReady.bind(this);
      }

      get sectionId() {
        return this.dataset.sectionId;
      }

      get productId() {
        return Number(this.dataset.productId);
      }

      get productFormId() {
        return `product-form-${this.sectionId}`;
      }

      get variantIdInput() {
        return this.productForm?.querySelector('[name="id"]');
      }

      get productForm() {
        return this.closest('product-info')?.querySelector(`form[is="product-form"]#${this.productFormId}`);
      }

      get sellingPlanInput() {
        return (
          this.closest('product-info')?.querySelector(`[name="selling_plan"][form="${this.productFormId}"]`) ||
          this.productForm?.querySelector('[name="selling_plan"]')
        );
      }

      get itemsContainer() {
        return this.querySelector('[data-bundle-items]');
      }

      get messageElement() {
        return this.querySelector('[data-bundle-message]');
      }

      get totalElement() {
        return this.querySelector('[data-bundle-total]');
      }

      get compareTotalElement() {
        return this.querySelector('[data-bundle-compare-total]');
      }

      get baseComparePriceElement() {
        return this.querySelector('[data-bundle-base-compare-price]');
      }

      get basePriceElement() {
        return this.querySelector('[data-bundle-base-price]');
      }

      get submitButton() {
        return this.querySelector('[data-bundle-submit]');
      }

      get productInfo() {
        return this.closest('product-info');
      }

      get designMode() {
        return this.dataset.designMode === 'true';
      }

      get oneTimeBundleDiscount() {
        return Number(this.dataset.oneTimeDiscount || 0);
      }

      get subscriptionBundleDiscount() {
        return Number(this.dataset.subscriptionDiscount || 0);
      }

      get autoIncludeSingleItem() {
        return this.dataset.autoIncludeSingleItem !== 'false';
      }

      connectedCallback() {
        this.currentVariant = this.getCurrentVariant();
        this.currentSellingPlanId = this.sellingPlanInput?.value || '';
        this.updateBaseRow();
        this.updateTotal();

        if (this.sellingPlanInput) {
          this.sellingPlanInput.addEventListener('change', this.handleSellingPlanChange);
        }

        document.addEventListener('rebuy:ready', this.handleRebuyReady);
        this.productInfo?.addEventListener('skio::update-selling-plan', this.handleSellingPlanChange);

        if (window.FoxTheme?.pubsub?.subscribe && this.sectionId) {
          this.variantChangeUnsubscriber = window.FoxTheme.pubsub.subscribe(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange
          );
        }

        if (!this.hasValidConfiguration()) {
          this.handleConfigurationError();
          return;
        }

        this.observeForLoad();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.variantChangeUnsubscriber?.();
        if (this.sellingPlanInput) {
          this.sellingPlanInput.removeEventListener('change', this.handleSellingPlanChange);
        }
        document.removeEventListener('rebuy:ready', this.handleRebuyReady);
        this.productInfo?.removeEventListener('skio::update-selling-plan', this.handleSellingPlanChange);
      }

      observeForLoad() {
        if (this.loaded || this.loading) return;

        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0]?.isIntersecting) return;
            observer.disconnect();
            this.loadProducts();
          },
          { rootMargin: '0px 0px 320px 0px' }
        );

        observer.observe(this);
      }

      handleRebuyReady() {
        if (this.loaded || this.loading) return;

        if (this.dataset.dataSourceId && this.getRebuyApiKey()) {
          this.hidden = false;
          this.clearMessage();
          this.observeForLoad();
        }
      }

      handleVariantChange(event) {
        if (event?.data?.sectionId !== this.sectionId) return;

        this.currentVariant = event.data.variant;
        this.dataset.variantId = this.currentVariant?.id || '';
        this.updateBaseRow();
        this.updateTotal();

        if (this.hasValidConfiguration()) {
          this.loadProducts(true);
        }
      }

      handleSellingPlanChange(event) {
        if (event?.detail?.variant) {
          this.currentVariant = event.detail.variant;
        }

        if (event?.detail?.sellingPlan?.id) {
          this.currentSellingPlanId = String(event.detail.sellingPlan.id);
        } else if (event?.detail?.sellingPlan === null) {
          this.currentSellingPlanId = '';
        } else if (event?.target?.name === 'selling_plan') {
          this.currentSellingPlanId = event.target.value || '';
        } else {
          this.currentSellingPlanId = this.sellingPlanInput?.value || this.currentSellingPlanId || '';
        }

        this.updateBaseRow();
        this.updateTotal();
      }

      hasValidConfiguration() {
        return Boolean(this.dataset.dataSourceId && this.getRebuyApiKey());
      }

      handleConfigurationError() {
        const message =
          this.dataset.configMessage ||
          'Add a Rebuy Public API Key and Data Source ID to load bundle products.';

        if (this.designMode) {
          this.setMessage(message);
        } else {
          this.hidden = true;
        }
      }

      getRebuyApiKey() {
        return (
          this.dataset.apiKey ||
          window.Rebuy?.shop?.api_key ||
          window.Rebuy?.shop?.public_api_key ||
          window.Rebuy?.api_key ||
          window.Rebuy?.apiKey ||
          window.Rebuy?.Config?.api_key ||
          window.Rebuy?.Config?.apiKey ||
          ''
        );
      }

      getCurrentVariant() {
        const variantJson = this.productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (variantJson) {
          try {
            return JSON.parse(variantJson);
          } catch (error) {
            console.error(error);
          }
        }

        const fallbackVariantId = Number(this.dataset.variantId || this.variantIdInput?.value);
        const fallbackPrice = Number(this.dataset.basePrice || 0);

        if (!fallbackVariantId) return null;

        return {
          id: fallbackVariantId,
          price: fallbackPrice,
          available: true,
          selling_plan_allocations: [],
        };
      }

      getCurrentSellingPlanId() {
        return this.sellingPlanInput?.value || this.currentSellingPlanId || '';
      }

      getCurrentBasePrice() {
        const variant = this.currentVariant || this.getCurrentVariant();
        const sellingPlanId = this.getCurrentSellingPlanId();

        if (sellingPlanId && variant?.selling_plan_allocations?.length) {
          const allocation = variant.selling_plan_allocations.find((item) => {
            const allocationId = item.selling_plan_id || item.selling_plan?.id;
            return String(allocationId) === String(sellingPlanId);
          });

          if (allocation?.price != null) {
            return Number(allocation.price);
          }
        }

        return Number(variant?.price || this.dataset.basePrice || 0);
      }

      getCurrentBaseComparePrice() {
        const variant = this.currentVariant || this.getCurrentVariant();
        if (!variant) return 0;

        const currentBasePrice = this.getCurrentBasePrice();
        const sellingPlanId = this.getCurrentSellingPlanId();
        const variantPrice = Number(variant.price || 0);
        const compareAtPrice = Number(variant.compare_at_price || 0);

        if (sellingPlanId && variant?.selling_plan_allocations?.length) {
          const allocation = variant.selling_plan_allocations.find((item) => {
            const allocationId = item.selling_plan_id || item.selling_plan?.id;
            return String(allocationId) === String(sellingPlanId);
          });

          const allocationComparePrice = Number(allocation?.compare_at_price || 0);
          if (allocationComparePrice > currentBasePrice) {
            return allocationComparePrice;
          }
        }

        if (compareAtPrice > currentBasePrice) {
          return compareAtPrice;
        }

        if (sellingPlanId && variantPrice > currentBasePrice) {
          return variantPrice;
        }

        return 0;
      }

      updateBaseRow() {
        const basePrice = this.getCurrentBasePrice();
        const comparePrice = this.getCurrentBaseComparePrice();
        this.dataset.basePrice = String(basePrice);

        if (this.basePriceElement) {
          this.basePriceElement.textContent = this.formatMoney(basePrice);
        }

        if (this.baseComparePriceElement) {
          this.baseComparePriceElement.hidden = comparePrice <= basePrice;
          this.baseComparePriceElement.textContent = comparePrice > basePrice ? this.formatMoney(comparePrice) : '';
        }

        if (this.submitButton) {
          const available = this.currentVariant?.available !== false;
          this.submitButton.disabled = !available;
          this.submitButton.setAttribute('aria-disabled', String(!available));
        }
      }

      buildRequestUrl() {
        const params = new URLSearchParams({
          key: this.getRebuyApiKey(),
          shopify_product_ids: String(this.productId),
          limit: String(this.dataset.productsToShow || 3),
        });

        const variantId = this.currentVariant?.id || this.dataset.variantId;
        if (variantId) {
          params.set('shopify_variant_ids', String(variantId));
        }

        const customerId = window.ShopifyAnalytics?.meta?.page?.customerId;
        if (customerId) {
          params.set('shopify_customer_id', String(customerId));
        }

        return `https://rebuyengine.com/api/v1/custom/id/${this.dataset.dataSourceId}?${params.toString()}`;
      }

      async loadProducts(force = false) {
        if ((this.loaded && !force) || this.loading) return;
        if (!this.hasValidConfiguration()) return;

        this.abortController?.abort();
        this.abortController = new AbortController();
        this.loading = true;
        this.classList.add('is-loading');
        this.setMessage(this.dataset.loadingText || 'Loading bundle options...');

        try {
          const response = await fetch(this.buildRequestUrl(), { signal: this.abortController.signal });
          const payload = await response.json();
          const products = Array.isArray(payload?.data) ? payload.data : [];
          this.renderProducts(products);
          this.loaded = true;
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(error);
            this.renderProducts([]);
            this.setMessage(this.dataset.errorText || 'Bundle products are unavailable right now.');
          }
        } finally {
          this.loading = false;
          this.classList.remove('is-loading');
        }
      }

      renderProducts(products) {
        const currentProductId = String(this.productId);
        const seen = new Set();

        const filteredProducts = products
          .filter((product) => product && String(product.id) !== currentProductId)
          .filter((product) => {
            if (seen.has(String(product.id))) return false;
            seen.add(String(product.id));
            return true;
          })
          .slice(0, Number(this.dataset.productsToShow || 3));

        const forceIncludeSingleItem = this.autoIncludeSingleItem && filteredProducts.length === 1;
        const normalizedProducts = filteredProducts
          .map((product, index) => this.renderProductRow(product, index, { forceIncluded: forceIncludeSingleItem }))
          .filter(Boolean);

        this.itemsContainer.innerHTML = normalizedProducts.join('');

        if (normalizedProducts.length === 0) {
          if (this.designMode) {
            this.setMessage(this.dataset.emptyText || 'No products were returned by this Rebuy data source.');
          } else {
            this.hidden = true;
          }
        } else {
          this.hidden = false;
          this.clearMessage();
          this.bindItemEvents();
        }

        this.updateTotal();
      }

      renderProductRow(product, index, options = {}) {
        const variant = this.getPurchasableVariant(product?.variants || []);
        if (!variant?.id) return '';

        const image = this.getProductImage(product);
        const title = this.escapeHtml(product.title || '');
        const variantTitle = this.getVariantTitle(variant.title || '');
        const checkboxId = `${this.id}-item-${variant.id}`;
        const price = this.normalizeMoney(variant.price);
        const forceIncluded = options.forceIncluded === true;

        if (forceIncluded) {
          return `
            <div class="product-rebuy-bundle__row product-rebuy-bundle__row--option product-rebuy-bundle__row--included is-selected">
              <span class="product-rebuy-bundle__status">Included</span>
              <span class="product-rebuy-bundle__media">
                ${image ? `<img src="${image}" alt="${title}" loading="lazy" width="96" height="96">` : ''}
              </span>
              <span class="product-rebuy-bundle__content">
                <span class="product-rebuy-bundle__title">${title}</span>
                ${variantTitle ? `<span class="product-rebuy-bundle__variant">${this.escapeHtml(variantTitle)}</span>` : ''}
              </span>
              <span class="product-rebuy-bundle__price">${this.formatMoney(price)}</span>
              <input type="hidden" name="bundle-product-forced" value="${variant.id}" data-price="${price}">
            </div>
          `;
        }

        return `
          <label class="product-rebuy-bundle__row product-rebuy-bundle__row--option" for="${checkboxId}">
            <span class="product-rebuy-bundle__checkbox">
              <input
                class="product-rebuy-bundle__checkbox-input"
                type="checkbox"
                id="${checkboxId}"
                name="bundle-product"
                value="${variant.id}"
                data-price="${price}"
              >
              <span class="product-rebuy-bundle__checkbox-ui"></span>
            </span>
            <span class="product-rebuy-bundle__media">
              ${image ? `<img src="${image}" alt="${title}" loading="lazy" width="96" height="96">` : ''}
            </span>
            <span class="product-rebuy-bundle__content">
              <span class="product-rebuy-bundle__title">${title}</span>
              ${variantTitle ? `<span class="product-rebuy-bundle__variant">${this.escapeHtml(variantTitle)}</span>` : ''}
            </span>
            <span class="product-rebuy-bundle__price">${this.formatMoney(price)}</span>
          </label>
        `;
      }

      getPurchasableVariant(variants) {
        return variants.find((variant) => variant.available !== false) || variants[0] || null;
      }

      getProductImage(product) {
        const firstImage = product?.images?.[0];
        if (!firstImage) return '';

        if (typeof firstImage === 'string') return firstImage;
        return firstImage.src || firstImage.url || '';
      }

      getVariantTitle(title) {
        if (!title || title === 'Default Title') return '';
        return title;
      }

      bindItemEvents() {
        this.querySelectorAll('input[name="bundle-product"]').forEach((input) => {
          this.syncItemState(input);
          input.addEventListener('change', () => {
            this.syncItemState(input);
            this.updateTotal();
          });
        });
      }

      syncItemState(input) {
        input.closest('.product-rebuy-bundle__row--option')?.classList.toggle('is-selected', input.checked);
      }

      getIncludedBundleProducts() {
        const seen = new Set();
        const includedProducts = [];

        this.querySelectorAll('input[name="bundle-product"]:checked, input[name="bundle-product-forced"]').forEach((input) => {
          const variantId = Number(input.value || input.dataset.variantId);
          if (!variantId || seen.has(variantId)) return;

          seen.add(variantId);
          includedProducts.push({
            id: variantId,
            price: Number(input.dataset.price || 0),
          });
        });

        return includedProducts;
      }

      updateTotal() {
        let total = this.getCurrentBasePrice();
        const includedProducts = this.getIncludedBundleProducts();

        includedProducts.forEach((product) => {
          total += Number(product.price || 0);
        });

        const compareTotal = total;
        const appliedDiscount = includedProducts.length > 0 ? this.getApplicableDiscount() : 0;
        total = Math.max(total - appliedDiscount, 0);

        if (this.totalElement) {
          this.totalElement.textContent = this.formatMoney(total);
        }

        if (this.compareTotalElement) {
          const showCompareTotal = appliedDiscount > 0;
          this.compareTotalElement.hidden = !showCompareTotal;
          this.compareTotalElement.textContent = showCompareTotal ? this.formatMoney(compareTotal) : '';
        }
      }

      getApplicableDiscount() {
        return this.getCurrentSellingPlanId() ? this.subscriptionBundleDiscount : this.oneTimeBundleDiscount;
      }

      normalizeMoney(value) {
        if (value == null || value === '') return 0;

        if (typeof value === 'string') {
          const normalizedValue = value.replace(/,/g, '').trim();
          if (!normalizedValue) return 0;

          if (normalizedValue.includes('.')) {
            return Math.round(Number(normalizedValue) * 100);
          }

          return Number(normalizedValue);
        }

        return Number(value) || 0;
      }

      formatMoney(amount) {
        if (window.FoxTheme?.Currency?.formatMoney) {
          return window.FoxTheme.Currency.formatMoney(amount, window.FoxTheme.settings?.moneyFormat);
        }

        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(amount, window.FoxTheme?.settings?.moneyFormat);
        }

        return `$${(amount / 100).toFixed(2)}`;
      }

      setMessage(message) {
        if (!this.messageElement) return;
        this.messageElement.hidden = false;
        this.messageElement.textContent = message;
      }

      clearMessage() {
        if (!this.messageElement) return;
        this.messageElement.hidden = true;
        this.messageElement.textContent = '';
      }

      escapeHtml(value) {
        return String(value)
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#39;');
      }
    }
  );
}
